/**
 * Matches an absolute or protocol-relative URL (e.g. `https://…`, `data:…`,
 * `blob:…`, `//host/path`).
 */
const ABSOLUTE_URL_PATTERN = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * Returns the browser's current origin composed from `window.location`
 * `protocol`, `hostname`, and `port`.
 *
 * Composed manually (rather than reading `window.location.origin`) to match
 * the convention used by `getStandaloneOrigin` in `bnp-core` and to remain
 * compatible with tests that mock `window.location` with only those fields.
 */
function getCurrentOrigin(): string {
  const { protocol, hostname, port } = window.location;
  const portSuffix = port ? `:${port}` : "";
  return `${protocol}//${hostname}${portSuffix}`;
}

/**
 * Resolve a notification deep-link URL for use with `window.open`.
 *
 * - Absolute URLs (e.g. `https://host/path`, `data:…`, protocol-relative
 *   `//host/…`) are returned unchanged.
 * - Path-absolute URLs (leading `/`) are prefixed with the browser's current
 *   origin so the deep link always opens against the host the user is
 *   currently on.
 * - Bare-relative URLs (e.g. `foo/bar`, `./foo/bar`) are passed through
 *   unchanged; the browser will resolve them against the current document.
 */
export function resolveDeepLinkUrl(url: string): string {
  if (ABSOLUTE_URL_PATTERN.test(url)) return url;
  if (url.startsWith("/")) return `${getCurrentOrigin()}${url}`;
  return url;
}
